if (!customElements.get('card-product')) {
  /**
   * 注入延后加载的图片。tp-image-hydrate.js 以 defer 方式在 layout 中加载；
   * 若首轮交互时尚未就绪，应保留监听并重试。
   */
  function hydrateDeferredImages(root) {
    if (!root) return 0
    // 自带兜底：不依赖 TPImage 是否已加载，避免部分卡片 hover 时注入器还没就绪。
    let count = 0
    const apply = (el) => {
      if (!el || !el.getAttribute) return
      const srcset = el.getAttribute('data-tp-srcset')
      const src = el.getAttribute('data-tp-src')
      if (!srcset && !src) return

      el.classList.remove('loaded')
      const wrapper = el.closest?.('.media-wrapper')
      if (wrapper) {
        wrapper.classList.remove('loaded')
        wrapper.classList.add('loading')
      }

      // 先清 data-tp-*，再写真实地址，减少和 image-lazy MO 的交叉。
      if (srcset) el.removeAttribute('data-tp-srcset')
      if (src) el.removeAttribute('data-tp-src')
      el.setAttribute('loading', 'eager')
      if (srcset) el.srcset = srcset
      if (src) el.src = src

      const done = () => {
        el.classList.add('loaded')
        if (wrapper) {
          wrapper.classList.remove('loading')
          wrapper.classList.add('loaded')
        }
      }
      if (typeof el.decode === 'function') {
        el.decode().then(done).catch(done)
      } else if (el.complete) {
        done()
      } else {
        el.addEventListener('load', done, { once: true })
        el.addEventListener('error', () => wrapper?.classList.remove('loading'), { once: true })
      }
      count += 1
    }

    if (root.matches?.('img[data-tp-src], img[data-tp-srcset], source[data-tp-srcset]')) {
      apply(root)
    }
    root.querySelectorAll?.('source[data-tp-srcset]').forEach(apply)
    root.querySelectorAll?.('img[data-tp-src], img[data-tp-srcset]').forEach(apply)

    if (count === 0 && window.TPImage && typeof window.TPImage.hydrate === 'function') {
      return window.TPImage.hydrate(root)
    }
    return count
  }

  function hydrateDeferredImagesWhenReady(root, attempts) {
    const count = hydrateDeferredImages(root)
    if (count > 0) return count
    if (!root || (attempts || 0) >= 40) return 0
    // 仍有待注入图片时才重试；已注入完则 query 为空，直接结束。
    if (!root.querySelector || !root.querySelector('img[data-tp-src], img[data-tp-srcset], source[data-tp-srcset]')) {
      return 0
    }
    setTimeout(function () {
      hydrateDeferredImagesWhenReady(root, (attempts || 0) + 1)
    }, 50)
    return 0
  }

  let secondImagePointerBound = false

  /**
   * 用 document 级 pointerover 做兜底。
   * 部分 Windows / 触控板环境下，挂在 <card-product> 上的 mouseenter
   * 不一定稳定触发；pointerover 会冒泡，更可靠。仍只在交互时注入。
   */
  function bindSecondImagePointerHydrate() {
    if (secondImagePointerBound) return
    secondImagePointerBound = true

    document.addEventListener(
      'pointerover',
      function (event) {
        const target = event.target
        if (!target || !target.closest) return
        const card = target.closest('card-product')
        if (!card) return
        const secondImage = card.querySelector('.product-card__image--second')
        if (!secondImage) return
        if (!secondImage.querySelector('img[data-tp-src], img[data-tp-srcset]')) return
        hydrateDeferredImagesWhenReady(secondImage)
      },
      { passive: true }
    )
  }

  class CardProduct extends HTMLElement {
    constructor() {
      super();
    }

    get colorOptions () {
        return this.querySelectorAll('.product-card__swatches-container .swatches .swatch-color')
    }

    get selectedColorOption() {
        return this.querySelector('.product-card__swatches-container .swatches input:checked')
    }

    get colorImages () {
        return this.querySelectorAll('.product-card__image-wrapper .product-card__image .product-cover-image')
    }

    get secondImage () {
        return this.querySelector('.product-card__image--second')
    }

    /**
     * 变体封面通过 data-tp-cover 匹配。
     * product-cover-list 写的是 variant.title（多选项常为 "1 PCS / 001 NOVA"），
     * 色块 input.value 通常只有颜色项（"001 NOVA"），需兼容后缀匹配。
     */
    findCoverImage(value) {
        if (value === undefined || value === null) return null
        const images = this.colorImages
        if (!images?.length) return null
        const raw = String(value)
        for (const image of images) {
            if (image.dataset.tpCover === raw) return image
        }
        const needle = raw.trim().toLowerCase()
        if (!needle) return null
        for (const image of images) {
            const cover = String(image.dataset.tpCover || '').trim()
            if (!cover || cover === 'first') continue
            const lower = cover.toLowerCase()
            if (
                lower === needle ||
                lower.endsWith(' / ' + needle) ||
                lower.endsWith('/ ' + needle) ||
                lower.endsWith('/' + needle)
            ) {
                return image
            }
        }
        return null
    }

    connectedCallback() {
        bindSecondImagePointerHydrate()
        this.initSecondImageLoading()
        setTimeout(() => {
            this.initFirstOption()
            this.initFirstOptionImage()
            this.colorOptions?.forEach((item) => {
                item.addEventListener('click', this.handleOptionClick.bind(this))
                // 鼠标移到色块上就先把该色号的图取回来，点击时基本已经就绪。
                item.addEventListener('mouseenter', this.handleOptionHover.bind(this))
            })
        }, 200)
    }

    /**
     * hover 第二图只在桌面端显示，等到指针移入或键盘聚焦卡片时才真正加载。
     * 只有成功注入后才解绑；失败则保留监听并短时重试。
     */
    initSecondImageLoading() {
        const secondImage = this.secondImage
        if (!secondImage) return

        const load = () => {
            if (hydrateDeferredImages(secondImage) < 1) {
                hydrateDeferredImagesWhenReady(secondImage)
                return
            }
            this.removeEventListener('pointerenter', load)
            this.removeEventListener('mouseenter', load)
            this.removeEventListener('focusin', load)
        }

        this.addEventListener('pointerenter', load)
        this.addEventListener('mouseenter', load)
        this.addEventListener('focusin', load)
    }

    initFirstOption () {
        const colorOptions = this.colorOptions
        if (!colorOptions?.length) return
        const firstOption = colorOptions[0]
        const parentElement = firstOption.parentElement 
        const inputDom = parentElement.querySelector('input')
        inputDom.setAttribute('checked', true)
        this.updateByobLinks(inputDom)
    }

    initFirstOptionImage() {
        const colorImages = this.colorImages
        if (!colorImages?.length) return
        const firstOptionImage = colorImages[0]
        firstOptionImage.classList.remove('hidden')
    }

    getSwatchInputFromEvent(e) {
        const swatch = e.target?.closest?.('.swatch-color, label.swatch-color, a.swatch-color')
        if (!swatch) return null
        // radio/label：input 在同一 li；链接色块没有 input，无法切图
        const row = swatch.closest('li') || swatch.parentElement
        return row?.querySelector?.('input[data-id], input[type="radio"], input[type="checkbox"]') || null
    }

    handleOptionHover(e) {
        const inputDom = this.getSwatchInputFromEvent(e)
        if (!inputDom) return
        hydrateDeferredImagesWhenReady(this.findCoverImage(inputDom.value))
    }

    handleOptionClick(e) {
        const inputDom = this.getSwatchInputFromEvent(e)
        if (!inputDom) return
        e.preventDefault()
        e.stopPropagation()
        if (this.selectedColorOption) {
            const selectedCoverDom = this.findCoverImage(this.selectedColorOption.value)
            this.selectedColorOption.removeAttribute('checked')
            if (selectedCoverDom) {
                selectedCoverDom.classList.add('hidden')
            }
        }
        const firstCoverDom = this.findCoverImage('first')
        if (firstCoverDom && !firstCoverDom.classList.contains('hidden')) {
            firstCoverDom.classList.add('hidden')
        }
        const newSelectedCoverDom = this.findCoverImage(inputDom.value)
        if (newSelectedCoverDom) {
            // 先注入图片地址再显示，避免出现一帧空白。
            hydrateDeferredImagesWhenReady(newSelectedCoverDom)
            newSelectedCoverDom.classList.remove('hidden')
        }
        inputDom.setAttribute('checked', true)
        const reversedLinks = this.querySelectorAll('.reversed-link')
        if (reversedLinks?.length) {
            reversedLinks.forEach((dom) => {
                let href = dom.href
                if (!href) return
                const questionIndex = href.indexOf('?')
                if (questionIndex > -1) {
                    href = href.slice(0, questionIndex)
                }
                dom.href = href + '?variant=' + inputDom.dataset.id
            })
        }
        this.updateByobLinks(inputDom)
    }

    updateByobLinks(inputDom) {
        const variantId = inputDom?.dataset?.id
        if (!variantId) return
        const byobLinks = this.querySelectorAll('[data-byob-link]')
        byobLinks?.forEach((dom) => {
            const baseUrl = dom.dataset.byobBaseUrl || dom.href.split('?')[0]
            const separator = baseUrl.includes('?') ? '&' : '?'
            dom.href = `${baseUrl}${separator}byob_variant=${variantId}`
            dom.dataset.byobVariantId = variantId
        })
    }
 }
 customElements.define('card-product', CardProduct);
}
